import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { helperService } from '../../../services/helperService';

const QuickBookModal = ({ helper, onClose }) => {
  const navigate = useNavigate();
  const [selectedSlot, setSelectedSlot] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!helper?.id) {
        setError('Helper information not available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const { data, error: fetchError } = await helperService?.getHelperAvailability(helper?.id);
        
        if (fetchError) throw fetchError;
        
        // Transform availability data into user-friendly time slot options
        const slots = transformAvailabilityToSlots(data || []);
        setAvailableSlots(slots);
      } catch (err) {
        console.error('Error fetching helper availability:', err);
        setError('Unable to load available time slots');
        setAvailableSlots([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [helper?.id]);

  // Transform helper_availability data into formatted time slots
  const transformAvailabilityToSlots = (availabilityData) => {
    const now = new Date();
    const slots = [];
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Get next 7 days
    for (let i = 0; i < 7; i++) {
      const targetDate = new Date(now);
      targetDate?.setDate(now?.getDate() + i);
      const dayOfWeek = targetDate?.getDay();
      
      // Find availability for this day
      const dayAvailability = availabilityData?.filter(slot => slot?.dayOfWeek === dayOfWeek);
      
      dayAvailability?.forEach(slot => {
        const [hours, minutes] = slot?.timeSlot?.split(':');
        const slotDateTime = new Date(targetDate);
        slotDateTime?.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        // Only include future slots
        if (slotDateTime > now) {
          const dayLabel = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : daysOfWeek?.[dayOfWeek];
          const timeLabel = formatTime(hours, minutes);
          
          slots?.push({
            value: slotDateTime?.toISOString(),
            label: `${dayLabel} at ${timeLabel}`,
            date: slotDateTime
          });
        }
      });
    }
    
    // Sort by date and return first 5 slots
    return slots?.sort((a, b) => a?.date - b?.date)?.slice(0, 5)?.map(({ value, label }) => ({ value, label }));
  };

  // Format time to 12-hour format
  const formatTime = (hours, minutes) => {
    const hour = parseInt(hours);
    const min = minutes?.padStart(2, '0');
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${min} ${period}`;
  };

  const handleConfirmBooking = () => {
    navigate('/booking-scheduling', {
      state: {
        helperId: helper?.id,
        helperName: helper?.name,
        selectedSlot
      }
    });
  };

  const handleViewFullCalendar = () => {
    navigate('/booking-scheduling', {
      state: {
        selectedHelper: helper
      }
    });
  };

  return (
    <div className="fixed inset-0 z-[900] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-lg border border-border shadow-xl max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Quick Book Session</h2>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border">
          <Image
            src={helper?.profileImage}
            alt={helper?.profileImageAlt}
            className="w-16 h-16 rounded-lg object-cover"
          />
          <div>
            <h3 className="font-semibold text-foreground mb-1">{helper?.name}</h3>
            <p className="text-sm text-muted-foreground">{helper?.typeLabel}</p>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-muted-foreground">Loading available slots...</span>
            </div>
          ) : error ? (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-sm text-destructive">
              {error}
            </div>
          ) : availableSlots?.length === 0 ? (
            <div className="bg-muted rounded-lg p-4 text-sm text-muted-foreground text-center">
              No available time slots found. Please try the full calendar view.
            </div>
          ) : (
            <Select
              label="Select Available Time Slot"
              options={availableSlots}
              value={selectedSlot}
              onChange={setSelectedSlot}
              placeholder="Choose a time..."
              required
            />
          )}

          <div className="bg-muted rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Icon name="Clock" size={16} className="text-primary" />
              <span className="text-foreground font-medium">Session Duration: 50 minutes</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Icon name="Video" size={16} className="text-primary" />
              <span className="text-muted-foreground">Video session via secure platform</span>
            </div>
          </div>

          <button
            onClick={handleViewFullCalendar}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors text-sm font-medium text-foreground"
          >
            <Icon name="Calendar" size={16} />
            <span>Need more options? View full calendar</span>
          </button>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            fullWidth
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            fullWidth
            iconName="Calendar"
            iconPosition="left"
            onClick={handleConfirmBooking}
            disabled={!selectedSlot || loading}
          >
            Confirm Booking
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickBookModal;